/**
 * applyChestRepair — the three.js seam for ADR-021: extract each skinned
 * mesh's bind-space skin data, run the kernel's Stage-B bounded-influence
 * repair (weights/bound-influence), and seat the repaired JOINTS/WEIGHTS back
 * into the buffer attributes in place. Mirrors rescue-orphans.ts: attributes
 * are patched with `needsUpdate` so everything downstream — isolation,
 * binding, EXPORT — inherits the repair, and the caller receives a typed
 * per-mesh outcome it must surface as evidence (LAWS: automatic is fine,
 * silent is not; refused ≠ not-needed ≠ repaired, ADR-021 §7).
 *
 * Iterates ALL skinned meshes (never `soleSkinnedMesh`): VRMs ship
 * face/body/hair as separate primitives, and the comparative sweep reads them
 * all. A mesh that carries no clavicle weight at all short-circuits to
 * `not-needed` WITHOUT voxelizing (the face mesh fast path) — measured as a
 * palette scan, not assumed from the mesh's name.
 *
 * The kernel does the law; this module only ferries data: bind-space
 * positions via `mesh.bindMatrix` (the same space as the RigDescriptor's
 * worldBind, both derived from the IBMs), joint names from the skeleton, and
 * the voxel substrate from `voxelizeVolume` — one grid per mesh, shared by
 * measurement and repair.
 *
 * Depends on: three, @harryhausen/kernel. Depended on by: app session driver
 * (run-document), the chest-integrity specimen probes.
 */
import { Matrix4, Object3D, SkinnedMesh, Vector3 } from 'three';
import {
  boundClavicleInfluence,
  resolveChestBones,
  voxelizeVolume,
  type BoundInfluenceOptions,
  type BoundInfluenceOutcome,
  type ChestBones,
  type Ontology,
  type RigDescriptor,
  type SkinVertexData,
  type VolumeGrid,
} from '@harryhausen/kernel';

/** One mesh's extracted skin data + the mesh-space triangle list. */
export interface ExtractedSkin {
  readonly skin: SkinVertexData & { readonly positions: Float64Array };
  readonly indices: Uint16Array | Uint32Array | null;
}

/**
 * Extract a skinned mesh's bind-space skin data in the kernel's plain-array
 * shape. Positions are carried through `mesh.bindMatrix` into skeleton bind
 * world — the space the RigDescriptor's `worldBind` lives in.
 */
export function extractSkinData(mesh: SkinnedMesh): ExtractedSkin {
  const position = mesh.geometry.getAttribute('position');
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  const n = position.count;
  const positions = new Float64Array(n * 3);
  const jointIndices = new Uint16Array(n * 4);
  const jointWeights = new Float64Array(n * 4);
  const v = new Vector3();
  const bind = new Matrix4().copy(mesh.bindMatrix);
  for (let i = 0; i < n; i += 1) {
    v.fromBufferAttribute(position, i).applyMatrix4(bind);
    positions[i * 3] = v.x;
    positions[i * 3 + 1] = v.y;
    positions[i * 3 + 2] = v.z;
    for (let k = 0; k < 4; k += 1) {
      jointIndices[i * 4 + k] = skinIndex.getComponent(i, k);
      jointWeights[i * 4 + k] = skinWeight.getComponent(i, k);
    }
  }
  const index = mesh.geometry.getIndex();
  return {
    skin: {
      positions,
      jointIndices,
      jointWeights,
      jointNames: mesh.skeleton.bones.map((b) => b.name),
    },
    indices: index ? (index.array as Uint16Array | Uint32Array) : null,
  };
}

/** One mesh's outcome, in the §7 vocabulary. */
export interface ChestRepairMeshResult {
  readonly mesh: string;
  readonly outcome: BoundInfluenceOutcome | { readonly kind: 'no-armed-weight' };
}

export interface ChestRepairResult {
  /** The resolved cast, or the reason the rig is out of class. */
  readonly resolution: ReturnType<typeof resolveChestBones>;
  readonly meshes: readonly ChestRepairMeshResult[];
}

/**
 * Voxel resolution OF THE CHEST CROP (not the body). Full-body voxelization is
 * both too coarse and too slow at the load door (measured: 64 on a 1.8m rig =
 * 2.8cm voxels — the whole envelope spans ~3 voxels and the cull quantizes
 * into the deltoid; 128 fixes precision but voxelizes for tens of seconds).
 * The Dionne-style answer is region-of-interest: voxelize only the chest box
 * (acromia ± 2 clavicle lengths, ~4 clavicle lengths long) at 64 → ~16 voxels
 * per clavicle length, a grid ~30× smaller than the body at 128. Vertices
 * outside the crop read Infinity and the envelope calls them 0 — exactly what
 * a full-body geodesic beyond two clavicle lengths would conclude.
 */
const CHEST_VOXEL_RESOLUTION = 64;
const CHEST_CROP_MARGIN_CLAVICLES = 2;

/**
 * Voxelize the chest region of a skin: triangles with any corner inside the
 * chest box, compacted to a standalone mesh, through the shared substrate.
 */
export function voxelizeChestRegion(
  skin: SkinVertexData,
  indices: ArrayLike<number> | null,
  bones: ChestBones,
): VolumeGrid {
  const anchors = [bones.clavicleL, bones.clavicleR, bones.armL, bones.armR].map((b) => b.worldPosition);
  const clavLen =
    (Math.hypot(
      bones.armL.worldPosition.x - bones.clavicleL.worldPosition.x,
      bones.armL.worldPosition.y - bones.clavicleL.worldPosition.y,
      bones.armL.worldPosition.z - bones.clavicleL.worldPosition.z,
    ) +
      Math.hypot(
        bones.armR.worldPosition.x - bones.clavicleR.worldPosition.x,
        bones.armR.worldPosition.y - bones.clavicleR.worldPosition.y,
        bones.armR.worldPosition.z - bones.clavicleR.worldPosition.z,
      )) /
    2;
  const margin = CHEST_CROP_MARGIN_CLAVICLES * clavLen;
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (const p of anchors) {
    for (const [a, c] of [p.x, p.y, p.z].entries()) {
      if (c - margin < min[a]!) min[a] = c - margin;
      if (c + margin > max[a]!) max[a] = c + margin;
    }
  }
  const inBox = (v: number): boolean => {
    const x = skin.positions[v * 3]!;
    const y = skin.positions[v * 3 + 1]!;
    const z = skin.positions[v * 3 + 2]!;
    return x >= min[0]! && x <= max[0]! && y >= min[1]! && y <= max[1]! && z >= min[2]! && z <= max[2]!;
  };
  const triCount = indices === null ? Math.floor(skin.positions.length / 9) : Math.floor(indices.length / 3);
  const cropPositions: number[] = [];
  for (let t = 0; t < triCount; t += 1) {
    const a = indices === null ? t * 3 : indices[t * 3]!;
    const b = indices === null ? t * 3 + 1 : indices[t * 3 + 1]!;
    const c = indices === null ? t * 3 + 2 : indices[t * 3 + 2]!;
    if (!inBox(a) && !inBox(b) && !inBox(c)) continue;
    for (const v of [a, b, c]) {
      cropPositions.push(skin.positions[v * 3]!, skin.positions[v * 3 + 1]!, skin.positions[v * 3 + 2]!);
    }
  }
  return voxelizeVolume(cropPositions, null, { resolution: CHEST_VOXEL_RESOLUTION });
}

/**
 * Run the ADR-021 repair over every skinned mesh under `root`. Repaired
 * attributes are seated in place; the caller surfaces the outcomes. A rig
 * whose chest cast cannot resolve returns `not-applicable` in `resolution`
 * with zero mesh work done — out of the defect class, loudly (§7/§10).
 */
/**
 * How far a clavicle's distal seed may snap onto the chest volume, by the BIND.
 * ARMS DOWN (Meshy): the shoulder seed sits in the air gap between the hanging
 * arm and the torso, and a long snap would seat the clavicle in ARM flesh — the
 * kernel's 3 voxels stands (the committed Meshy pin). T-POSE (arms within 30°
 * of level — every ActorCore-preset Tripo): nothing but shoulder beside the
 * seed; the Tripo joint offset leaves it 3.1–5.2 voxels off, so the repair
 * refused on ALL of them and their chests compressed on arms-down (Allen, the
 * wolf lad, 2026-09-25). 6 voxels there; every one then repairs, culling 5–10%
 * of clavicle mass to the spine (the calibrated male: ~11%).
 */
const T_POSE_MAX_DROOP_DEG = 30;
const T_POSE_SEED_SNAP_VOXELS = 6;
function seedSnapFor(rig: RigDescriptor, bones: ChestBones): number | undefined {
  const droop = (arm: ChestBones['armL']): number | null => {
    const elbow = rig.bones.find((b) => b.parent === arm.rigBone);
    if (elbow === undefined) return null;
    const d = { x: elbow.worldBind.position.x - arm.worldPosition.x, y: elbow.worldBind.position.y - arm.worldPosition.y, z: elbow.worldBind.position.z - arm.worldPosition.z };
    const len = Math.hypot(d.x, d.y, d.z);
    if (!(len > 0)) return null;
    const down = -(d.x * bones.up.x + d.y * bones.up.y + d.z * bones.up.z) / len;
    return (Math.asin(Math.max(-1, Math.min(1, down))) * 180) / Math.PI;
  };
  const l = droop(bones.armL);
  const r = droop(bones.armR);
  return l !== null && r !== null && l < T_POSE_MAX_DROOP_DEG && r < T_POSE_MAX_DROOP_DEG ? T_POSE_SEED_SNAP_VOXELS : undefined;
}

export function applyChestRepair(
  root: Object3D,
  rig: RigDescriptor,
  ontology: Ontology,
  options: BoundInfluenceOptions = {},
): ChestRepairResult {
  const resolution = resolveChestBones(rig, ontology);
  if (resolution.kind !== 'resolved') return { resolution, meshes: [] };
  const bones = resolution.bones;

  const meshes: SkinnedMesh[] = [];
  root.traverse((node) => {
    if ((node as SkinnedMesh).isSkinnedMesh === true) meshes.push(node as SkinnedMesh);
  });

  const results: ChestRepairMeshResult[] = [];
  for (const mesh of meshes) {
    const position = mesh.geometry.getAttribute('position');
    const skinIndex = mesh.geometry.getAttribute('skinIndex');
    const skinWeight = mesh.geometry.getAttribute('skinWeight');
    if (!position || !skinIndex || !skinWeight) continue;

    // Fast path: a mesh that gives NO ARMED BONE any weight (face, hair)
    // cannot carry the defect — measured, not assumed. The scan covers both
    // armed classes (clavicles AND their arm subtrees): a clothing primitive
    // with in-band arm bleed but zero clavicle weight is still a patient
    // (Codex P2, #86).
    const armedPalette = new Set<number>();
    mesh.skeleton.bones.forEach((b, i) => {
      if (bones.subtreeL.has(b.name) || bones.subtreeR.has(b.name)) armedPalette.add(i);
    });
    let armedMass = 0;
    for (let i = 0; i < skinIndex.count && armedMass === 0; i += 1) {
      for (let k = 0; k < 4; k += 1) {
        if (armedPalette.has(skinIndex.getComponent(i, k)) && skinWeight.getComponent(i, k) > 0) {
          armedMass += skinWeight.getComponent(i, k);
        }
      }
    }
    if (armedMass === 0) {
      results.push({ mesh: mesh.name || '(unnamed mesh)', outcome: { kind: 'no-armed-weight' } });
      continue;
    }

    const { skin, indices } = extractSkinData(mesh);
    let outcome: BoundInfluenceOutcome;
    try {
      const grid: VolumeGrid = voxelizeChestRegion(skin, indices, bones);
      const snap = options.seedSnapLimitVoxels === undefined ? seedSnapFor(rig, bones) : undefined;
      outcome = boundClavicleInfluence(grid, skin, indices, bones, snap === undefined ? options : { ...options, seedSnapLimitVoxels: snap });
    } catch (e) {
      // §7: a chest region that cannot voxelize refuses; the file ships unrepaired.
      outcome = { kind: 'refused', reasons: [e instanceof Error ? e.message : String(e)], markers: [] };
    }
    if (outcome.kind === 'repaired') {
      for (let i = 0; i < skinIndex.count; i += 1) {
        for (let k = 0; k < 4; k += 1) {
          skinIndex.setComponent(i, k, outcome.jointIndices[i * 4 + k]!);
          skinWeight.setComponent(i, k, outcome.jointWeights[i * 4 + k]!);
        }
      }
      skinIndex.needsUpdate = true;
      skinWeight.needsUpdate = true;
    }
    results.push({ mesh: mesh.name || '(unnamed mesh)', outcome });
  }
  return { resolution, meshes: results };
}

/** Shared voxel substrate for callers that also measure (the gate probes). */
export function voxelizeSkinnedMesh(mesh: SkinnedMesh, bones: ChestBones): { extracted: ExtractedSkin; grid: VolumeGrid } {
  const extracted = extractSkinData(mesh);
  return {
    extracted,
    grid: voxelizeChestRegion(extracted.skin, extracted.indices, bones),
  };
}

export type { ChestBones };
